/**
 * Business Landing Page
 *
 * SEO-optimized landing page targeting businesses and enterprises.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, useStructuredData, SITE_CONFIG } from '../../lib/seo';
import PublicLayout from '../../components/layout/PublicLayout';

export default function BusinessPage() {
  const { i18n } = useTranslation();

  useSEO({
    title: 'Business Form Builder | Surveys, Feedback & Data Collection | xtFORM',
    description: 'Collect customer feedback, conduct employee surveys, and streamline data collection. Enterprise-grade security, API integration, team collaboration. Free to start.',
    keywords: 'business forms, customer feedback forms, employee surveys, data collection, enterprise forms, secure forms, API integration, team collaboration, lead generation forms',
    url: `${SITE_CONFIG.domain}/business`,
    locale: i18n.language,
  });

  useHreflang('/business');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Business Form Builder',
    'description': 'Enterprise-grade form builder for businesses of all sizes.',
    'url': `${SITE_CONFIG.domain}/business`,
    'mainEntity': {
      '@type': 'SoftwareApplication',
      'name': 'xtFORM for Business',
      'applicationCategory': 'BusinessApplication',
      'operatingSystem': 'Web',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD',
      },
    },
  };

  useStructuredData(structuredData, 'business-page');

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6">
              <span className="text-lg">💼</span>
              Enterprise-Ready
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Forms That Drive
              <span className="text-blue-600"> Business Results</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              From customer feedback to lead generation, create professional forms that integrate seamlessly
              with your business workflows. Secure, scalable, and completely free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
              >
                Start Building for Free
              </Link>
              <Link
                to="/docs/api"
                className="px-8 py-4 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-colors"
              >
                Explore API
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Solutions for Every Business Need
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whether you're collecting leads, gathering feedback, or conducting research, we've got you covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer Feedback</h3>
              <p className="text-gray-600">
                Understand your customers better with satisfaction surveys, NPS scores, and feedback forms.
                Make data-driven decisions to improve your products.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Lead Generation</h3>
              <p className="text-gray-600">
                Capture leads with customizable forms. Integrate with your CRM via our API
                and automate your sales pipeline.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">👥</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Employee Surveys</h3>
              <p className="text-gray-600">
                Measure employee engagement, conduct 360 reviews, and gather anonymous feedback
                to build a better workplace.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Market Research</h3>
              <p className="text-gray-600">
                Conduct surveys and polls to understand market trends, test product concepts,
                and validate business ideas.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Registration Forms</h3>
              <p className="text-gray-600">
                Create event registrations, membership signups, and onboarding forms
                with file uploads and signatures.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🔗</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">API Integration</h3>
              <p className="text-gray-600">
                Connect forms to your existing tools and workflows. Our REST API lets you
                automate everything programmatically.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🛍️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">E-Commerce & Orders</h3>
              <p className="text-gray-600">
                Sell products online with our built-in e-commerce features. Create product pages,
                manage inventory, and accept payments seamlessly.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🌐</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Custom Domains</h3>
              <p className="text-gray-600">
                Connect your own domain to product pages and store portals via CNAME.
                Build your brand with shop.yourdomain.com.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Enterprise-Grade Security
              </h2>
              <p className="text-gray-600 mb-8">
                Your data security is our top priority. We implement industry-leading security measures
                to protect your business and customer information.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">AES-256 Encryption</h4>
                    <p className="text-gray-600 text-sm">All sensitive data is encrypted at rest and in transit</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">GDPR Compliant</h4>
                    <p className="text-gray-600 text-sm">Full compliance with European data protection regulations</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Team Permissions</h4>
                    <p className="text-gray-600 text-sm">Granular access controls and role-based permissions</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">99.9% Uptime</h4>
                    <p className="text-gray-600 text-sm">Built on Cloudflare's global edge network</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Scale</h3>
              <p className="text-blue-100 mb-6">
                Our platform is built to handle millions of submissions. Whether you have
                100 or 100,000 responses, performance stays consistent.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">Unlimited</div>
                  <div className="text-blue-200 text-sm">Forms</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">Unlimited</div>
                  <div className="text-blue-200 text-sm">Responses</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">50+</div>
                  <div className="text-blue-200 text-sm">Field Types</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">REST</div>
                  <div className="text-blue-200 text-sm">API Access</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Start Collecting Data Today
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Join thousands of businesses using xtFORM to streamline their data collection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-block px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-colors"
            >
              Create Free Account
            </Link>
            <Link
              to="/docs/api"
              className="inline-block px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-colors"
            >
              View API Docs
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
